import React, {FC, useEffect, useState} from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  CircularProgress,
  Divider,
  Alert,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {Link as RouterLink} from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import FavoriteIcon from '@mui/icons-material/Favorite';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import CloseIcon from '@mui/icons-material/Close';
import {useSnackbar} from 'notistack';
import {useGetApiQuery, useSubmitDataWithRerenderMutation} from '../../../services/goroskop';
import {getZodiacTitle} from '../../../utils/zodiac';
import {getEasternTitle} from '../../../utils/easternZodiac';
import {ZodiacMatrixTable} from './ZodiacMatrixTable';
import {EasternMatrixTable} from './EasternMatrixTable';
import {NumberMatrixTable} from './NumberMatrixTable';

const DEFAULT_CONFIG = {
  intro: 'Вам успешно подобраны люди, удовлетворяющие вашим условиям поиска по возрасту.',
  incompatible: 'К сожалению Ваш бывший партнер с вами был несовместим.',
  astrologer: 'За подробностями обратитесь к Свату-Астрологу Вячеславу Горелову',
  check_title: 'Экспресс-проверка совместимости',
  check_subtitle: 'Вы можете прямо сейчас проверить любого бывшего партнера на несовместимость:'
};

export const WelcomeModalConfig: FC = () => {
  const {enqueueSnackbar} = useSnackbar();
  const {data, isLoading, isFetching} = useGetApiQuery('welcome_modal.php');
  const [submitData, {isLoading: isSaving}] = useSubmitDataWithRerenderMutation();

  const [intro, setIntro] = useState<string>(DEFAULT_CONFIG.intro);
  const [incompatible, setIncompatible] = useState<string>(DEFAULT_CONFIG.incompatible);
  const [astrologer, setAstrologer] = useState<string>(DEFAULT_CONFIG.astrologer);
  const [checkTitle, setCheckTitle] = useState<string>(DEFAULT_CONFIG.check_title);
  const [checkSubtitle, setCheckSubtitle] = useState<string>(DEFAULT_CONFIG.check_subtitle);
  const [previewStep, setPreviewStep] = useState<'form' | 'result'>('result');

  // Настройки пар знаков зодиака (Западный календарь)
  const [zodiacPairs, setZodiacPairs] = useState<Record<string, string>>(() => {
    try {
      const cached = localStorage.getItem('astro_zodiac_pairs');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // Настройки пар животных (Китайский календарь)
  const [easternPairs, setEasternPairs] = useState<Record<string, string>>(() => {
    try {
      const cached = localStorage.getItem('astro_eastern_pairs');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // Настройки пар по числам дня рождения (1–31)
  const [numberPairs, setNumberPairs] = useState<Record<string, string>>(() => {
    try {
      const cached = localStorage.getItem('astro_number_pairs');
      return cached ? JSON.parse(cached) : {};
    } catch (e) {
      return {};
    }
  });

  // Активная вкладка матриц совместимости
  const [compatTab, setCompatTab] = useState<'west' | 'east' | 'number'>('west');

  useEffect(() => {
    if (data && (data as any)[0]) {
      const item = (data as any)[0];
      if (item.intro) setIntro(item.intro);
      if (item.incompatible) setIncompatible(item.incompatible);
      if (item.astrologer) setAstrologer(item.astrologer);
      if (item.check_title) setCheckTitle(item.check_title);
      if (item.check_subtitle) setCheckSubtitle(item.check_subtitle);
      if (item.zodiac_pairs && typeof item.zodiac_pairs === 'object') {
        setZodiacPairs(item.zodiac_pairs);
        try {
          localStorage.setItem('astro_zodiac_pairs', JSON.stringify(item.zodiac_pairs));
        } catch (e) {}
      }
      if (item.eastern_pairs && typeof item.eastern_pairs === 'object') {
        setEasternPairs(item.eastern_pairs);
        try {
          localStorage.setItem('astro_eastern_pairs', JSON.stringify(item.eastern_pairs));
        } catch (e) {}
      }
      if (item.number_pairs && typeof item.number_pairs === 'object') {
        setNumberPairs(item.number_pairs);
        try {
          localStorage.setItem('astro_number_pairs', JSON.stringify(item.number_pairs));
        } catch (e) {}
      }
    }
  }, [data]);

  // Точечное сохранение отдельной пары на сервере (защищает остальные 143+ пар от затирания)
  const savePairToServer = async (
    pairType: 'zodiac' | 'eastern' | 'number',
    userSign: string,
    partnerSign: string,
    text: string,
    applyBothWays: boolean
  ) => {
    const fd = new FormData();
    fd.append('action', 'save_pair');
    fd.append('pair_type', pairType);
    fd.append('user_sign', userSign);
    fd.append('partner_sign', partnerSign);
    fd.append('text', text);
    fd.append('apply_both_ways', applyBothWays ? '1' : '0');

    try {
      const res: any = await submitData({name: 'welcome_modal.php', data: fd});
      if (res?.error || res?.data?.[0]?.status === false) {
        const errMsg = res?.data?.[0]?.msg || res?.error?.data?.msg || res?.error?.error || 'Ошибка сохранения на сервере';
        enqueueSnackbar(errMsg, {variant: 'error'});
        return false;
      }
      return true;
    } catch (err) {
      enqueueSnackbar('Ошибка соединения с сервером', {variant: 'error'});
      return false;
    }
  };

  // Точечное удаление отдельной пары на сервере
  const deletePairFromServer = async (
    pairType: 'zodiac' | 'eastern' | 'number',
    key: string,
    reverseKey?: string
  ) => {
    const fd = new FormData();
    fd.append('action', 'delete_pair');
    fd.append('pair_type', pairType);
    fd.append('key', key);
    if (reverseKey) {
      fd.append('reverse_key', reverseKey);
    }

    try {
      const res: any = await submitData({name: 'welcome_modal.php', data: fd});
      if (res?.error || res?.data?.[0]?.status === false) {
        const errMsg = res?.data?.[0]?.msg || res?.error?.data?.msg || res?.error?.error || 'Ошибка удаления на сервере';
        enqueueSnackbar(errMsg, {variant: 'error'});
        return false;
      }
      return true;
    } catch (err) {
      enqueueSnackbar('Ошибка соединения с сервером', {variant: 'error'});
      return false;
    }
  };

  // --- ОБРАБОТЧИКИ ЗАПАДНОГО ЗОДИАКА ---
  const handleSavePair = async (uSign: string, pSign: string, text: string, applyBothWays: boolean) => {
    const trimmed = text.trim();
    const key = `${uSign}_${pSign}`;
    const reverseKey = `${pSign}_${uSign}`;

    const next = {...zodiacPairs};
    if (!trimmed) {
      delete next[key];
      if (applyBothWays) delete next[reverseKey];
    } else {
      next[key] = trimmed;
      if (applyBothWays) next[reverseKey] = trimmed;
    }

    setZodiacPairs(next);
    try {
      localStorage.setItem('astro_zodiac_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await savePairToServer('zodiac', uSign, pSign, trimmed, applyBothWays);
    if (ok) {
      if (!trimmed) {
        enqueueSnackbar(`Сохранено: ответ для пары «${getZodiacTitle(uSign)} + ${getZodiacTitle(pSign)}» сброшен`, {
          variant: 'info',
          autoHideDuration: 3500
        });
      } else {
        enqueueSnackbar(`Сохранено! Пара «${getZodiacTitle(uSign)} + ${getZodiacTitle(pSign)}» успешно сохранена`, {
          variant: 'success',
          autoHideDuration: 3500
        });
      }
    }
  };

  const handleDeletePair = async (key: string, reverseKey?: string) => {
    const next = {...zodiacPairs};
    delete next[key];
    if (reverseKey) {
      delete next[reverseKey];
    }
    setZodiacPairs(next);
    try {
      localStorage.setItem('astro_zodiac_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await deletePairFromServer('zodiac', key, reverseKey);
    if (ok) {
      enqueueSnackbar('Сохранено: пара удалена из настроек', {
        variant: 'info',
        autoHideDuration: 3500
      });
    }
  };

  // --- ОБРАБОТЧИКИ КИТАЙСКОГО КАЛЕНДАРЯ ---
  const handleSaveEasternPair = async (uSign: string, pSign: string, text: string, applyBothWays: boolean) => {
    const trimmed = text.trim();
    const key = `${uSign}_${pSign}`;
    const reverseKey = `${pSign}_${uSign}`;

    const next = {...easternPairs};
    if (!trimmed) {
      delete next[key];
      if (applyBothWays) delete next[reverseKey];
    } else {
      next[key] = trimmed;
      if (applyBothWays) next[reverseKey] = trimmed;
    }

    setEasternPairs(next);
    try {
      localStorage.setItem('astro_eastern_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await savePairToServer('eastern', uSign, pSign, trimmed, applyBothWays);
    if (ok) {
      const uTitle = getEasternTitle(uSign);
      const pTitle = getEasternTitle(pSign);
      if (!trimmed) {
        enqueueSnackbar(`Сохранено: ответ для пары «${uTitle} + ${pTitle}» сброшен`, {
          variant: 'info',
          autoHideDuration: 3500
        });
      } else {
        enqueueSnackbar(`Сохранено! Пара «${uTitle} + ${pTitle}» успешно сохранена`, {
          variant: 'success',
          autoHideDuration: 3500
        });
      }
    }
  };

  const handleDeleteEasternPair = async (key: string, reverseKey?: string) => {
    const next = {...easternPairs};
    delete next[key];
    if (reverseKey) {
      delete next[reverseKey];
    }
    setEasternPairs(next);
    try {
      localStorage.setItem('astro_eastern_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await deletePairFromServer('eastern', key, reverseKey);
    if (ok) {
      enqueueSnackbar('Сохранено: пара удалена из настроек', {
        variant: 'info',
        autoHideDuration: 3500
      });
    }
  };

  // --- ОБРАБОТЧИКИ ЧИСЕЛ РОЖДЕНИЯ ---
  const handleSaveNumberPair = async (uNum: string, pNum: string, text: string, applyBothWays: boolean) => {
    const trimmed = text.trim();
    const key = `${uNum}_${pNum}`;
    const reverseKey = `${pNum}_${uNum}`;

    const next = {...numberPairs};
    if (!trimmed) {
      delete next[key];
      if (applyBothWays) delete next[reverseKey];
    } else {
      next[key] = trimmed;
      if (applyBothWays) next[reverseKey] = trimmed;
    }

    setNumberPairs(next);
    try {
      localStorage.setItem('astro_number_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await savePairToServer('number', uNum, pNum, trimmed, applyBothWays);
    if (ok) {
      if (!trimmed) {
        enqueueSnackbar(`Сохранено: ответ для чисел «${uNum} + ${pNum}» сброшен`, {
          variant: 'info',
          autoHideDuration: 3500
        });
      } else {
        enqueueSnackbar(`Сохранено! Пара чисел «${uNum} + ${pNum}» успешно сохранена`, {
          variant: 'success',
          autoHideDuration: 3500
        });
      }
    }
  };

  const handleDeleteNumberPair = async (key: string, reverseKey?: string) => {
    const next = {...numberPairs};
    delete next[key];
    if (reverseKey) {
      delete next[reverseKey];
    }
    setNumberPairs(next);
    try {
      localStorage.setItem('astro_number_pairs', JSON.stringify(next));
    } catch (e) {}

    const ok = await deletePairFromServer('number', key, reverseKey);
    if (ok) {
      enqueueSnackbar('Сохранено: пара чисел удалена из настроек', {
        variant: 'info',
        autoHideDuration: 3500
      });
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const fd = new FormData();
    fd.append('action', 'save_texts');
    fd.append('intro', intro);
    fd.append('incompatible', incompatible);
    fd.append('astrologer', astrologer);
    fd.append('check_title', checkTitle);
    fd.append('check_subtitle', checkSubtitle);
    fd.append('astrologer', astrologer);
    fd.append('check_title', checkTitle);
    fd.append('check_subtitle', checkSubtitle);
    fd.append('zodiac_pairs', JSON.stringify(zodiacPairs));
    fd.append('eastern_pairs', JSON.stringify(easternPairs));
    fd.append('number_pairs', JSON.stringify(numberPairs));

    try {
      const res: any = await submitData({name: 'welcome_modal.php', data: fd});
      if (res?.error || (res.data && res.data[0] && !res.data[0].status)) {
        const errMsg = res?.data?.[0]?.msg || res?.error?.data?.msg || res?.error?.error || 'Ошибка сохранения';
        enqueueSnackbar(errMsg, {variant: 'error'});
      } else {
        enqueueSnackbar(res?.data?.[0]?.msg || 'Тексты баннера успешно сохранены', {variant: 'success'});
      }
    } catch (err) {
      enqueueSnackbar('Ошибка соединения с сервером', {variant: 'error'});
    }
  };

  if (isLoading) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress/>
      </Box>
    );
  }

  return (
    <Box sx={{maxWidth: 1200, mx: 'auto', p: {xs: 1.5, sm: 3}}}>
      {/* ШАПКА РАЗДЕЛА */}
      <Box
        sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2}}>
        <Box sx={{display: 'flex', alignItems: 'center', gap: 1.5}}>
          <IconButton component={RouterLink} to="/admin/config" sx={{color: 'text.secondary'}}>
            <ArrowBackIcon/>
          </IconButton>
          <Box>
            <Typography variant="h5" sx={{fontWeight: 700, color: '#1a237e'}}>
              Настройка приветственной модалки
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Управление текстами приветствия, формой экспресс-проверки и матрицами совместимости
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* БЛОК 1: НАСТРОЙКИ ТЕКСТОВ МОДАЛКИ */}
      <Paper
        sx={{
          p: {xs: 2, sm: 3},
          mb: 4,
          borderRadius: 2.5,
          bgcolor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)'
        }}
        elevation={0}
      >
        <Typography variant="h6" sx={{fontWeight: 700, mb: 1, color: '#263238'}}>
          1. Тексты модального окна
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
          Настройте формулировки, которые отображаются посетителю в приветственном окне при первом входе
        </Typography>

        <Box component="form" onSubmit={handleSave}>
          <Stack spacing={2.5}>
            <TextField
              label="1. Текст успешного подбора анкет (Интро)"
              multiline
              minRows={2}
              fullWidth
              value={intro}
              onChange={(e) => setIntro(e.target.value)}
              helperText="Показывается в самом начале (по умолчанию: Вам успешно подобраны люди, удовлетворяющие вашим условиям поиска по возрасту.)"
            />

            <TextField
              label="2. Заголовок экспресс-проверки"
              fullWidth
              value={checkTitle}
              onChange={(e) => setCheckTitle(e.target.value)}
              helperText="Заголовок перед полем ввода даты (по умолчанию: Экспресс-проверка совместимости)"
            />

            <TextField
              label="3. Подзаголовок экспресс-проверки"
              multiline
              minRows={2}
              fullWidth
              value={checkSubtitle}
              onChange={(e) => setCheckSubtitle(e.target.value)}
              helperText="Поясняющий текст перед датой (по умолчанию: Вы можете прямо сейчас проверить совместимость по дате рождения партнера:)"
            />

            <TextField
              label="4. Вердикт несовместимости"
              multiline
              minRows={2}
              fullWidth
              value={incompatible}
              onChange={(e) => setIncompatible(e.target.value)}
              helperText="Показывается после 1-секундной проверки даты рождения партнера"
            />

            <TextField
              label="5. Обращение к астрологу"
              multiline
              minRows={2}
              fullWidth
              value={astrologer}
              onChange={(e) => setAstrologer(e.target.value)}
              helperText="Поясняющий текст перед кнопкой связи (VK)"
            />

            <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap', pt: 0.5}}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                size="large"
                startIcon={isSaving ? <CircularProgress size={20} color="inherit"/> : <SaveIcon/>}
                disabled={isSaving || isFetching}
                sx={{px: 4, fontWeight: 700, textTransform: 'none'}}
              >
                {isSaving ? 'Сохранение...' : 'Сохранить тексты баннера'}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Paper>

      {/* БЛОК 2: НАСТРОЙКИ СОВМЕСТИМОСТИ (ВКЛАДКИ) */}
      <Paper
        sx={{
          p: {xs: 2, sm: 2.5},
          mb: 4,
          borderRadius: 2.5,
          bgcolor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.12)'
        }}
        elevation={0}
      >
        <Box sx={{mb: 2}}>
          <Typography variant="subtitle1" sx={{fontWeight: 700, color: '#1a237e', mb: 0.5}}>
            Настройки совместимости для экспресс-проверки
          </Typography>
          <Typography variant="body2" sx={{color: 'text.secondary'}}>
            Задайте персональный текст ответа для комбинаций западного зодиака, китайского календаря (животные) и чисел
            дня рождения. Кликните по любой ячейке в таблице для настройки. Все изменения сохраняются автоматически.
          </Typography>
        </Box>

        {/* Табы переключения систем совместимости */}
        <Box sx={{borderBottom: 1, borderColor: 'divider', mb: 2}}>
          <Tabs
            value={compatTab}
            onChange={(e, val) => setCompatTab(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 700,
                fontSize: '0.88rem',
                textTransform: 'none',
                minHeight: 44,
                px: {xs: 1.5, sm: 2.5}
              }
            }}
          >
            <Tab
              value="west"
              label={
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <span>♈ Западные знаки зодиака</span>
                  <Chip
                    size="small"
                    label={Object.keys(zodiacPairs).length}
                    color={Object.keys(zodiacPairs).length > 0 ? 'primary' : 'default'}
                    sx={{height: 20, fontSize: '0.72rem', fontWeight: 700}}
                  />
                </Box>
              }
            />
            <Tab
              value="east"
              label={
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <span>🐅 Китайский календарь</span>
                  <Chip
                    size="small"
                    label={Object.keys(easternPairs).length}
                    color={Object.keys(easternPairs).length > 0 ? 'secondary' : 'default'}
                    sx={{height: 20, fontSize: '0.72rem', fontWeight: 700}}
                  />
                </Box>
              }
            />
            <Tab
              value="number"
              label={
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                  <span>🔢 Числа рождения</span>
                  <Chip
                    size="small"
                    label={Object.keys(numberPairs).length}
                    color={Object.keys(numberPairs).length > 0 ? 'success' : 'default'}
                    sx={{height: 20, fontSize: '0.72rem', fontWeight: 700}}
                  />
                </Box>
              }
            />
          </Tabs>
        </Box>

        {compatTab === 'west' && (
          <ZodiacMatrixTable
            zodiacPairs={zodiacPairs}
            onSavePair={handleSavePair}
            onDeletePair={handleDeletePair}
          />
        )}

        {compatTab === 'east' && (
          <EasternMatrixTable
            easternPairs={easternPairs}
            onSavePair={handleSaveEasternPair}
            onDeletePair={handleDeleteEasternPair}
          />
        )}

        {compatTab === 'number' && (
          <NumberMatrixTable
            numberPairs={numberPairs}
            onSavePair={handleSaveNumberPair}
            onDeletePair={handleDeleteNumberPair}
          />
        )}
      </Paper>

      {/* Предпросмотр */}
      <Paper
        sx={{
          p: {xs: 2, sm: 3},
          borderRadius: 2,
          bgcolor: 'transparent',
          background: 'transparent',
          boxShadow: 'none',
          border: '1px solid rgba(0, 0, 0, 0.1)'
        }}
        elevation={0}
      >
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
          flexWrap: 'wrap',
          gap: 1.5
        }}>
          <Typography variant="subtitle2" sx={{fontWeight: 700, color: '#546e7a', textTransform: 'uppercase'}}>
            Предпросмотр отображения в модалке:
          </Typography>
          <ToggleButtonGroup
            value={previewStep}
            exclusive
            onChange={(e, val) => val && setPreviewStep(val)}
            size="small"
          >
            <ToggleButton value="form" sx={{textTransform: 'none', fontWeight: 600, px: 2}}>
              Шаг 1: Форма с датой
            </ToggleButton>
            <ToggleButton value="result" sx={{textTransform: 'none', fontWeight: 600, px: 2}}>
              Шаг 2: Результат проверки
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Paper
          elevation={3}
          sx={{
            p: {xs: 1.5, sm: 2.5},
            borderRadius: {xs: 2.5, sm: 3},
            maxWidth: {xs: '100%', sm: 520},
            mx: 'auto',
            background: 'linear-gradient(180deg, #ffffff 0%, #f9fbff 100%)',
            boxShadow: '0 12px 36px rgba(25, 118, 210, 0.15)',
            border: '1px solid #e3f2fd'
          }}
        >
          {/* Кнопка закрытия модалки */}
          <Box sx={{display: 'flex', justifyContent: 'flex-end', mb: 0.5}}>
            <IconButton size="small" disabled sx={{color: 'grey.400'}}>
              <CloseIcon fontSize="small"/>
            </IconButton>
          </Box>

          {/* Заголовок и вступительный текст */}
          <Box sx={{textAlign: 'center', mb: {xs: 1.5, sm: 2.5}}}>
            <Typography variant="h6" component="div" sx={{
              fontWeight: 700,
              color: '#1a237e',
              lineHeight: 1.25,
              fontSize: {xs: '1.05rem', sm: '1.25rem'}
            }}>
              Здравствуйте!
            </Typography>
            <Typography variant="body1" sx={{
              color: '#455a64',
              mt: {xs: 0.5, sm: 1},
              px: {xs: 0.5, sm: 2},
              fontSize: {xs: '0.82rem', sm: '0.95rem'},
              lineHeight: {xs: 1.35, sm: 1.45}
            }}>
              {intro || DEFAULT_CONFIG.intro}
            </Typography>
          </Box>

          {/* Плашка со счетчиком кандидатов */}
          <Paper
            elevation={0}
            sx={{
              p: {xs: 1, sm: 1.8},
              mb: {xs: 1.5, sm: 2.5},
              backgroundColor: '#eef5fc',
              borderRadius: {xs: 2, sm: 2.5},
              border: '1px solid #c9e0fa'
            }}
          >
            <Box sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'center',
              gap: {xs: 0.8, sm: 1.5}
            }}>
              <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8}}>
                <PeopleAltIcon sx={{color: '#1976d2', fontSize: {xs: 20, sm: 24}}}/>
                <Typography variant="body1"
                            sx={{fontWeight: 700, color: '#37474f', fontSize: {xs: '0.84rem', sm: '0.95rem'}}}>
                  Их количество составило:
                </Typography>
              </Box>

              <Typography
                variant="h6"
                component="span"
                sx={{
                  fontWeight: 800,
                  color: '#1565c0',
                  fontSize: {xs: '1.25rem', sm: '1.5rem'},
                  lineHeight: 1,
                  px: {xs: 0.8, sm: 1.2},
                  py: {xs: 0.2, sm: 0.3},
                  bgcolor: '#ffffff',
                  borderRadius: 1.5,
                  border: '1px solid #bbdefb',
                  boxShadow: '0 1px 4px rgba(25,118,210,0.1)'
                }}
              >
                23 818
              </Typography>

              <Typography variant="body2" sx={{color: '#546e7a', fontSize: {xs: '0.75rem', sm: '0.85rem'}}}>
                (Основная база: <b>142</b> • Подписчики ВК: <b>23 676</b>)
              </Typography>
            </Box>
          </Paper>

          {/* Карточка экспресс-проверки / результатов */}
          <Paper
            elevation={0}
            sx={{
              p: {xs: 1.5, sm: 2.2},
              backgroundColor: '#ffffff',
              borderRadius: {xs: 2, sm: 2.5},
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 10px rgba(0,0,0,0.03)'
            }}
          >
            {previewStep === 'form' ? (
              <>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.8, mb: {xs: 0.5, sm: 1}}}>
                  <FavoriteIcon sx={{color: '#e91e63', fontSize: {xs: 18, sm: 22}}}/>
                  <Typography variant="subtitle1"
                              sx={{fontWeight: 700, color: '#263238', fontSize: {xs: '0.88rem', sm: '1rem'}}}>
                    {checkTitle}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{
                  color: '#546e7a',
                  mb: {xs: 1.2, sm: 2},
                  fontSize: {xs: '0.76rem', sm: '0.875rem'},
                  lineHeight: {xs: 1.35, sm: 1.43}
                }}>
                  {checkSubtitle}
                </Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={{xs: 1, sm: 1.5}} alignItems="stretch">
                  <TextField
                    size="small"
                    fullWidth
                    label="Дата рождения партнера"
                    value="15.06.1995"
                    disabled
                    sx={{
                      flex: 1,
                      '& .MuiInputBase-input': {
                        fontSize: {xs: '0.82rem', sm: '0.92rem'},
                        py: {xs: 0.7, sm: 1}
                      },
                      '& .MuiInputLabel-root': {
                        fontSize: {xs: '0.8rem', sm: '0.88rem'}
                      }
                    }}
                  />
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setPreviewStep('result')}
                    sx={{
                      height: {xs: 36, sm: 40},
                      px: {xs: 2, sm: 3},
                      whiteSpace: 'nowrap',
                      width: {xs: '100%', sm: 'auto'},
                      fontWeight: 600,
                      fontSize: {xs: '0.82rem', sm: '0.875rem'},
                      boxShadow: 'none'
                    }}
                  >
                    Проверить
                  </Button>
                </Stack>
              </>
            ) : (
              <Box>
                <Alert
                  severity="warning"
                  icon={<CheckCircleOutlineIcon fontSize="inherit" sx={{fontSize: {xs: 18, sm: 22}}}/>}
                  sx={{
                    borderRadius: 2,
                    backgroundColor: '#fff8e1',
                    border: '1px solid #ffe082',
                    color: '#5d4037',
                    p: {xs: 1, sm: 1.5},
                    '& .MuiAlert-icon': {
                      color: '#f57c00'
                    }
                  }}
                >
                  <Typography variant="subtitle2"
                              sx={{fontWeight: 700, mb: 0.5, fontSize: {xs: '0.85rem', sm: '0.95rem'}}}>
                    {incompatible || DEFAULT_CONFIG.incompatible}
                  </Typography>
                  <Typography variant="body2" sx={{
                    mb: 1,
                    color: '#4e342e',
                    fontSize: {xs: '0.78rem', sm: '0.875rem'},
                    lineHeight: {xs: 1.35, sm: 1.45}
                  }}>
                    {astrologer || DEFAULT_CONFIG.astrologer}
                  </Typography>
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={
                        <Box
                          component="img"
                          src="/images/social/vk.svg"
                          alt="VK"
                          sx={{
                            width: {xs: 16, sm: 18},
                            height: {xs: 16, sm: 18},
                            borderRadius: '4px',
                            display: 'block'
                          }}
                        />
                      }
                      sx={{
                        py: {xs: 0.35, sm: 0.5},
                        px: {xs: 1, sm: 1.5},
                        fontSize: {xs: '0.75rem', sm: '0.8125rem'},
                        borderColor: '#0077ff',
                        color: '#0077ff',
                        fontWeight: 600,
                        '&:hover': {
                          borderColor: '#005bbf',
                          backgroundColor: 'rgba(0, 119, 255, 0.05)'
                        }
                      }}
                    >
                      <Box component="span" sx={{position: 'relative', top: '1px'}}>VK</Box>
                    </Button>
                  </Stack>
                </Alert>
              </Box>
            )}
          </Paper>

          {/* Нижняя кнопка перехода */}
          <Box sx={{pt: {xs: 1.5, sm: 2.5}}}>
            <Button
              variant="contained"
              fullWidth
              size="medium"
              disabled
              sx={{
                py: {xs: 0.9, sm: 1.3},
                fontWeight: 700,
                fontSize: {xs: '0.85rem', sm: '1rem'},
                textTransform: 'none',
                borderRadius: 2,
                backgroundColor: '#1976d2 !important',
                color: '#ffffff !important',
                boxShadow: '0 4px 14px rgba(25, 118, 210, 0.3)',
                opacity: 1
              }}
            >
              Перейти к анкетам
            </Button>
          </Box>
        </Paper>
      </Paper>
    </Box>
  );
};

export default WelcomeModalConfig;
